import Sortable from "sortablejs";
import { registerFeatureBootstrap } from "./bootstrap";

const LIST_SELECTOR = "[data-sortable-list]";
const ITEM_SELECTOR = "[data-sortable-id]";
const HANDLE_SELECTOR = "[data-sortable-handle]";
const ORDER_FIELD_SELECTOR = "[data-sortable-order]";
const MOVE_BUTTON_SELECTOR = "[data-sortable-move]";

type SortableListElement = HTMLElement & {
  __wmSortableInstance?: Sortable;
};

function normalizeText(value: unknown): string {
  return String(value ?? "").trim();
}

export function syncOrderField(list: HTMLElement): void {
  const form = list.closest("form");
  const orderField = form?.querySelector<HTMLInputElement>(ORDER_FIELD_SELECTOR);
  if (!(orderField instanceof HTMLInputElement)) {
    return;
  }
  const ordered = Array.from(list.querySelectorAll<HTMLElement>(ITEM_SELECTOR))
    .map((item) => normalizeText(item.dataset.sortableId))
    .filter(Boolean);
  orderField.value = ordered.join(",");
}

function dispatchSortableChange(list: HTMLElement): void {
  syncOrderField(list);
  list.dispatchEvent(new CustomEvent("wmu:sortable-change", { bubbles: true }));
}

export function moveItem(list: HTMLElement, item: HTMLElement, direction: "up" | "down"): void {
  const parent = item.parentElement;
  if (!(parent instanceof HTMLElement)) {
    return;
  }
  if (direction === "up") {
    const previous = item.previousElementSibling;
    if (previous) {
      parent.insertBefore(item, previous);
    }
  } else {
    const next = item.nextElementSibling;
    if (next) {
      parent.insertBefore(next, item);
    }
  }
  dispatchSortableChange(list);
}

function initMoveControls(list: HTMLElement): void {
  if (list.dataset.sortableMoveBound === "true") {
    return;
  }
  list.dataset.sortableMoveBound = "true";
  list.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    const button = target.closest<HTMLElement>(MOVE_BUTTON_SELECTOR);
    if (!(button instanceof HTMLElement)) {
      return;
    }
    const item = button.closest<HTMLElement>(ITEM_SELECTOR);
    if (!(item instanceof HTMLElement)) {
      return;
    }
    const direction = normalizeText(button.dataset.sortableMove);
    if (direction !== "up" && direction !== "down") {
      return;
    }
    if (!button.hasAttribute("aria-label")) {
      const title = item.querySelector<HTMLElement>("[data-sortable-title]")?.textContent?.trim() || "item";
      button.setAttribute("aria-label", `Move ${title} ${direction}`);
    }
    event.preventDefault();
    moveItem(list, item, direction);
  });
}

function initList(list: SortableListElement): void {
  if (list.dataset.sortableBound === "true") {
    syncOrderField(list);
    return;
  }
  list.dataset.sortableBound = "true";

  initMoveControls(list);
  syncOrderField(list);

  const itemSelector = normalizeText(list.dataset.sortableItemSelector) || ITEM_SELECTOR;
  const handleSelector = normalizeText(list.dataset.sortableHandle) || HANDLE_SELECTOR;
  const animation = Number.parseInt(normalizeText(list.dataset.sortableAnimation) || "150", 10);

  list.__wmSortableInstance = Sortable.create(list, {
    animation: Number.isFinite(animation) ? animation : 150,
    draggable: itemSelector,
    handle: handleSelector,
    ghostClass: "wm-sortable-ghost",
    chosenClass: "wm-sortable-chosen",
    dragClass: "wm-sortable-drag",
    forceFallback: false,
    onEnd() {
      dispatchSortableChange(list);
    },
  });
}

function destroyList(list: SortableListElement): void {
  list.__wmSortableInstance?.destroy();
  delete list.__wmSortableInstance;
  delete list.dataset.sortableBound;
  delete list.dataset.sortableMoveBound;
}

export function initSortableLists(scope: ParentNode = document): void {
  scope.querySelectorAll<SortableListElement>(LIST_SELECTOR).forEach((list) => {
    initList(list);
  });
}

declare global {
  interface Window {
    __WMU_SORTABLE_LISTS_BOOTED?: boolean;
    WMSortableLists?: {
      init: typeof initSortableLists;
      sync: typeof syncOrderField;
    };
  }
}

window.WMSortableLists = {
  init: initSortableLists,
  sync: syncOrderField,
};

if (!window.__WMU_SORTABLE_LISTS_BOOTED) {
  window.__WMU_SORTABLE_LISTS_BOOTED = true;
  registerFeatureBootstrap({
    key: "sortable:lists",
    selector: LIST_SELECTOR,
    mount(root) {
      initList(root as SortableListElement);
    },
    unmount(root) {
      destroyList(root as SortableListElement);
    },
  });
}
